import FormErrors from "@/components/FormErrors/FormErrors";
import HmAutocompleteObject from "@/components/Autocomplete/AutocompleteObject.vue";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation";
import uuidv4 from "uuid/v4";
import { JSConn } from "@/utilities/js-conn";
import { map as Rmap, find as Rfind, propEq as RpropEq } from "ramda";
import { format } from "date-fns";

export default {
  name: "workOrderEditForm",
  props: ["workOrderId"],
  components: {
    FormErrors,
    HmAutocompleteObject,
    LoadingAnimation,
  },
  data() {
    return {
      dataLoaded: false,
      loadingMessage: "Loading Work Order...Please be patient...",
      hasFormErrors: false,
      formErrors: [],
      workOrder: {
        id: "",
        name: "",
        description: null,
        start_date: "",
        submitted_date: "",
        scheduled_date: "",
        completed_date: "",
        archived_date: "",
        cut_number: 1,
        duration: 1,
        client_field: {},
        client_field_id: null,
        commodity_id: null,
        commodity: {},
        acres: 0,
      },
      client_field_id: null,
    };
  },
  methods: {
    submitForm(evt) {
      evt.preventDefault();
      const jsConn = new JSConn();

      jsConn.formParams = this.workOrder;
      // first need to save the work order
      let storeDispatch;
      if (this.workOrderId === "new") {
        storeDispatch = "createWorkOrder";
      } else {
        storeDispatch = "updateWorkOrder";
      }

      this.$store.dispatch(storeDispatch, jsConn).then(() => {
        this.$router.push({
          name: "workOrderTasks",
          params: { workOrderId: this.workOrder.id },
        });
        return Promise.resolve();
      });
    },
    updateClientField(evt) {
      this.workOrder.client_field = evt;
      this.workOrder.client_field_id = evt.value;
      this.client_field_id = evt.value;

      const scf = this.selectedClientField;
      this.workOrder.commodity_id = scf.commodity_id;
      this.workOrder.acres = scf.acres;

      // load the commodity
      const updatedCommodity = Rfind(
        RpropEq("value", scf.commodity_id),
        this.commoditiesForAutocomplete,
      );
      this.$refs.commodity.setResultParent(updatedCommodity);
    },
    updateCommodity(evt) {
      this.workOrder.commodity = evt;
      this.workOrder.commodity_id = evt.value;
    },
    loadDefaultLineItems(items) {
      return Rmap(item => {
        return {
          description: item.description,
          id: uuidv4(),
          item_date: null,
          line_item_id: item.id,
          quantity: 0,
          rate: item.default_rate,
        };
      }, items);
    },
    showDeleteModal() {
      this.$refs["deleteModal"].show();
    },
    hideDeleteModal() {
      this.$refs["deleteModal"].hide();
    },
    deleteWorkOrder() {
      const jsConn = new JSConn();
      jsConn.toRoutePath = { name: "workOrders" };
      jsConn.formParams = {
        id: this.workOrderId,
      };
      this.$store.dispatch("deleteWorkOrder", jsConn).then(jsConn => {
        this.$router.push(jsConn.toRoutePath);
        return Promise.resolve(jsConn);
      });
    },
  },
  computed: {
    clientFields: function() {
      return this.$store.getters.clientFields;
    },
    clientFieldsForAutocomplete: function() {
      return this.$store.getters.clientFieldsForAutocomplete;
    },
    commoditiesForAutocomplete: function() {
      return this.$store.getters.commoditiesForAutocomplete;
    },
    selectedClientField: function() {
      return this.$store.getters.clientFieldById(this.client_field_id);
    },
    persistedWorkOrder: function() {
      return this.$store.getters.workOrder;
    },
  },
  created() {
    const jsConn = new JSConn();
    const jsConnClientFields = new JSConn();
    const jsConnCommodities = new JSConn();

    jsConnCommodities.formParams = {};

    jsConn.formParams = {
      includes: ["clients"],
    };

    const dataPromises = [
      this.$store.dispatch("needClientFields", jsConnClientFields),
      this.$store.dispatch("needCommodities", jsConnCommodities),
    ];

    if (this.workOrderId === "new") {
      // make the calls to the store to get the data
      Promise.all(dataPromises).then(() => {
        this.dataLoaded = true;
        const rightNow = new Date();
        const formattedDate = format(rightNow, "yyyy-MM-dd");

        this.workOrder.id = uuidv4();
        this.workOrder.name = "";
        this.workOrder.description = null;
        this.workOrder.status = "pending";
        this.workOrder.start_date = formattedDate;
        this.workOrder.submitted_date = formattedDate;
        this.workOrder.scheduled_date = formattedDate;
        this.workOrder.completed_date = null;
        this.workOrder.archived_date = null;
        this.workOrder.cut_number = 0;
        this.workOrder.duration = 1;
        this.workOrder.client_field_id = null;
        this.workOrder.commodity_id = null;
        this.workOrder.acres = 0;
        return Promise.resolve();
      });
    } else {
      // editing and existing work order
      // get the data necessary for that
      jsConn.formParams = {
        id: this.workOrderId,
      };

      dataPromises.push(this.$store.dispatch("needWorkOrder", jsConn));

      Promise.all(dataPromises).then(() => {
        this.dataLoaded = true;
        // load the data into the form
        this.workOrder.id = this.persistedWorkOrder.id;
        this.workOrder.name = this.persistedWorkOrder.name;
        this.workOrder.description = this.persistedWorkOrder.description;
        this.workOrder.status = this.persistedWorkOrder.status;
        this.workOrder.start_date = this.persistedWorkOrder.start_date;
        this.workOrder.submitted_date = this.persistedWorkOrder.submitted_date;
        this.workOrder.scheduled_date = this.persistedWorkOrder.scheduled_date;
        this.workOrder.completed_date = this.persistedWorkOrder.completed_date;
        this.workOrder.archived_date = this.persistedWorkOrder.archived_date;
        this.workOrder.cut_number = this.persistedWorkOrder.cut_number;
        this.workOrder.duration = this.persistedWorkOrder.duration;
        this.workOrder.client_field_id = this.persistedWorkOrder.client_field_id;
        this.workOrder.commodity_id = this.persistedWorkOrder.commodity_id;
        this.workOrder.acres = this.persistedWorkOrder.acres;

        // load the field
        const clientField = Rfind(
          RpropEq("value", this.persistedWorkOrder.client_field_id),
          this.clientFieldsForAutocomplete,
        );

        this.workOrder.client_field = clientField;

        // load the commodity
        this.workOrder.commodity = Rfind(
          RpropEq("value", this.persistedWorkOrder.commodity_id),
          this.commoditiesForAutocomplete,
        );

        return Promise.resolve();
      });
    }
  },
};
